export const WEGLOT_LANGUAGE_STORAGE_KEY = 'weglotLanguage';

/**
 * Somewhere to remember the translation language for the rest of the session.
 *
 * An interface rather than direct storage access so the resolver can be tested
 * without touching a real Storage, and so the backing store can change without
 * touching the resolution rules.
 */
export interface LanguageStore {
    read(): string | null;
    write(tag: string): void;
    clear(): void;
}

/**
 * Remember the language in `sessionStorage` — per tab, gone when the tab closes.
 *
 * Session rather than local scope is deliberate: the interview is a single-tab
 * linear flow, so a session is exactly the right lifetime, and a language picked
 * up from one link cannot follow the user around a shared device forever.
 *
 * Every access is guarded because storage is not always available — Safari's
 * private mode and blocked-cookie settings throw on access rather than returning
 * null. Localization is a progressive enhancement, so an unusable store means
 * English, never an error.
 */
export function createSessionLanguageStore(storage: Storage = sessionStorage): LanguageStore {
    return {
        read() {
            try {
                return storage.getItem(WEGLOT_LANGUAGE_STORAGE_KEY);
            } catch {
                return null;
            }
        },
        write(tag: string) {
            try {
                storage.setItem(WEGLOT_LANGUAGE_STORAGE_KEY, tag);
            } catch {
                // Nothing to recover: the language simply won't survive navigation.
            }
        },
        clear() {
            try {
                storage.removeItem(WEGLOT_LANGUAGE_STORAGE_KEY);
            } catch {
                // Nothing to recover.
            }
        },
    };
}
